import { useState, useEffect } from 'react';
import { Coordinates, CalculationMethod, PrayerTimes, Prayer, Qibla } from 'adhan';
import { Cloud, Sun, CloudRain, CloudSnow, User, Moon, Baby } from 'lucide-react';
import logo from '../assets/logo.png';
import { Language } from '../data/translations';

// Helper to get days until Ramadan
const getDaysUntilRamadan = () => {
  // Approximate date for Ramadan 2026 (Subject to moon sighting)
  // Feb 17, 2026 is the estimated start
  const ramadanStart = new Date('2026-02-17T00:00:00');
  const today = new Date();
  
  // Reset hours to compare just dates
  today.setHours(0,0,0,0);
  
  const diffTime = ramadanStart.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

import { UserProfile } from '../hooks/useProfile';

interface NavBarProps {
  lang: string;
  setLang: (lang: Language) => void;
  onProfileClick: () => void;
  profile: UserProfile;
  onToggleKidsMode: () => void;
}

export function NavBar({ lang, setLang, onProfileClick, profile, onToggleKidsMode }: NavBarProps) {
  const [weather, setWeather] = useState<{ temp: number; icon: React.ReactNode } | null>(null);
  const [nextPrayer, setNextPrayer] = useState<{ name: string; time: string } | null>(null);
  const [time, setTime] = useState(new Date());
  const [ramadanDays, setRamadanDays] = useState<number>(0);
  const [qibla, setQibla] = useState<number>(0);
  const [heading, setHeading] = useState<number>(0);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    setRamadanDays(getDaysUntilRamadan());
    return () => clearInterval(timer);
  }, []);

  // Location & Data Fetching
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      setLocation({ lat: latitude, lng: longitude });

      // 1. Qibla Direction (Angle from North)
      const qiblaDir = Qibla(new Coordinates(latitude, longitude));
      setQibla(qiblaDir);

      // 2. Prayer Times
      const coordinates = new Coordinates(latitude, longitude);
      const params = CalculationMethod.MuslimWorldLeague();
      const pt = new PrayerTimes(coordinates, new Date(), params);
      
      const next = pt.nextPrayer();
      if (next !== Prayer.None) {
        setNextPrayer({
          name: prayerName(next),
          time: pt.timeForPrayer(next)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || ''
        });
      }

      // 3. Weather
      const apiKey = import.meta.env.VITE_OPENWEATHER_API_KEY;
      if (apiKey) {
        try {
          const res = await fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${apiKey}&units=metric`);
          if (res.ok) {
            const data = await res.json();
            setWeather({
              temp: Math.round(data.main.temp),
              icon: getWeatherIcon(data.weather[0].main)
            });
          }
        } catch (e) { console.error(e); }
      }
    });
  }, []);

  // Compass Heading (Device Orientation)
  const requestCompassPermission = async () => {
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const permissionState = await (DeviceOrientationEvent as any).requestPermission();
        if (permissionState === 'granted') {
          window.addEventListener('deviceorientation', handleOrientation);
        } else {
          alert('Compass permission denied');
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleOrientation = (event: DeviceOrientationEvent) => {
    // webkitCompassHeading is for iOS, alpha is for Android (approx)
    const compass = (event as any).webkitCompassHeading || Math.abs(event.alpha! - 360);
    setHeading(compass);
  };

  useEffect(() => {
    // For non-iOS 13+ devices, add listener automatically
    if (window.DeviceOrientationEvent && typeof (DeviceOrientationEvent as any).requestPermission !== 'function') {
      window.addEventListener('deviceorientation', handleOrientation);
    }
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, []);

  const prayerName = (p: any) => {
    switch(p) {
      case Prayer.Fajr: return 'Fajr';
      case Prayer.Sunrise: return 'Sunrise';
      case Prayer.Dhuhr: return 'Dhuhr';
      case Prayer.Asr: return 'Asr';
      case Prayer.Maghrib: return 'Maghrib';
      case Prayer.Isha: return 'Isha';
      default: return '';
    }
  };

  const getWeatherIcon = (condition: string) => {
    switch (condition.toLowerCase()) {
      case 'clear': return <Sun className="w-4 h-4 text-amber-400" />;
      case 'clouds': return <Cloud className="w-4 h-4 text-slate-400" />;
      case 'rain': return <CloudRain className="w-4 h-4 text-blue-400" />;
      case 'snow': return <CloudSnow className="w-4 h-4 text-sky-200" />;
      default: return <Cloud className="w-4 h-4 text-slate-400" />;
    }
  };

  // Calculate rotation for the compass arrow to point to Mecca
  // Arrow should rotate relative to phone's heading.
  // If phone points North (0), arrow points to Qibla.
  // If phone turns right (90), arrow should turn left (-90) relative to screen to stay pointing at Qibla.
  // Rotation = Qibla - Heading

  return (
    <nav className="fixed bottom-0 w-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-t border-white/50 dark:border-slate-700/50 z-50 px-4 py-3 flex justify-between items-center shadow-lg safe-area-bottom">
      {/* Left: Logo & Time */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
           <img src={logo} alt="Habl" className="h-8 w-auto" />
           <div className="hidden sm:block">
             <h1 className="font-bold text-lg text-emerald-900 dark:text-emerald-400 leading-none">Habl</h1>
             <p className="text-[10px] text-stone-500 dark:text-slate-400">{time.toLocaleDateString([], { weekday: 'short', day: 'numeric' })}</p>
           </div>
        </div>
        
        {/* Mobile Time Compact */}
        <div className="sm:hidden border-l border-stone-200 dark:border-slate-700 pl-3">
           <p className="font-bold text-slate-800 dark:text-slate-200 text-sm leading-none uppercase">
             {time.toLocaleDateString([], { month: 'short', day: 'numeric' })}
           </p>
           <p className="text-[10px] text-stone-500 dark:text-slate-400 leading-none mt-0.5">
             {time.toLocaleDateString([], { weekday: 'long' })}
           </p>
        </div>
      </div>

      {/* Center: Sun/Moon Tracker (Absolute Center) */}

      {/* Right: Widgets */}
      <div className="flex items-center gap-3">
        
        {/* Ramadan Countdown (Only show if close, e.g. < 100 days) */}
        {ramadanDays > 0 && ramadanDays < 100 && (
          <div className="hidden lg:flex items-center gap-1.5 bg-emerald-50/80 dark:bg-emerald-900/30 px-2.5 py-1 rounded-full border border-emerald-100 dark:border-emerald-800" title="Days until Ramadan">
             <Moon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 fill-emerald-600/20" />
             <span className="text-[10px] font-bold text-emerald-800 dark:text-emerald-200 uppercase tracking-wide">
               {ramadanDays} Days
             </span>
          </div>
        )}

        {/* Time Display */}
        <span className="hidden md:block text-xs font-bold text-stone-600 font-mono">
          {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>

        {/* Sun/Moon Tracker (Advanced) */}

        {/* Qibla Compass (Detailed) */}
        {location && (
          <button 
            onClick={requestCompassPermission}
            className="relative w-10 h-10 flex items-center justify-center bg-slate-900 rounded-full border-2 border-slate-700 shadow-lg cursor-pointer hover:border-emerald-500 transition-colors overflow-hidden group" 
            title={`Qibla: ${Math.round(qibla)}° | Heading: ${Math.round(heading)}°`}
          >
             {/* 1. Fixed Top Indicator (Phone Direction) */}
             <div className="absolute top-0 w-0.5 h-1.5 bg-red-500 z-20 rounded-b-full shadow-[0_0_4px_rgba(239,68,68,0.8)]" />

             {/* 2. Rotating Dial (North & Qibla) */}
             <div 
               style={{ transform: `rotate(${-heading}deg)`, transition: 'transform 0.3s ease-out' }}
               className="w-full h-full relative"
             >
                {/* Dial Face */}
                <div className="absolute inset-0 rounded-full bg-[conic-gradient(from_0deg,var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-slate-800 opacity-90" />
                
                {/* Cardinal Points */}
                <span className="absolute top-1 left-1/2 -translate-x-1/2 text-[6px] font-bold text-slate-300">N</span>
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[6px] font-bold text-slate-500">S</span>
                <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[6px] font-bold text-slate-500">E</span>
                <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[6px] font-bold text-slate-500">W</span>

                {/* Ticks (SVG) */}
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-30">
                   <circle cx="50" cy="50" r="48" fill="none" stroke="white" strokeWidth="0.5" strokeDasharray="1 4" />
                </svg>

                {/* Qibla Marker (The Kaaba Direction) */}
                <div 
                  className="absolute top-0 left-1/2 -ml-1 h-1/2 origin-bottom flex flex-col items-center justify-start pt-1.5"
                  style={{ transform: `rotate(${qibla}deg)` }}
                >
                   <div className="w-2 h-2 bg-emerald-500 rounded-sm rotate-45 border border-emerald-300 shadow-[0_0_6px_rgba(16,185,129,0.8)] z-10" />
                   <div className="w-0.5 h-3 bg-emerald-500/50 rounded-full mt-0.5" />
                </div>
             </div>
          </button>
        )}

        {/* Prayer Time (Next) */}
        {nextPrayer && (
          <div className="hidden xs:flex flex-col items-end mr-2">
            <span className="text-[10px] text-stone-500 uppercase font-bold tracking-wider">Next: {nextPrayer.name}</span>
            <span className="text-xs font-bold text-emerald-600 font-mono">{nextPrayer.time}</span>
          </div>
        )}

        {/* Weather */}
        {weather && (
           <div className="flex items-center gap-1 bg-stone-50 px-2 py-1 rounded-lg border border-stone-100">
             {weather.icon}
             <span className="text-xs font-bold text-slate-700">{weather.temp}°</span>
           </div>
        )}

        {/* Kids Mode Toggle */}
        <button
          onClick={onToggleKidsMode}
          className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors border ${
            profile.settings.kidsMode
              ? 'bg-sky-100 text-sky-600 border-sky-200'
              : 'hover:bg-stone-100 dark:hover:bg-slate-800 text-stone-400 border-transparent'
          }`}
          title={profile.settings.kidsMode ? "Exit Kids Mode" : "Enter Kids Mode"}
        >
          <Baby className="w-5 h-5" />
        </button>

        {/* Menu / Lang */}
        <button 
           onClick={() => {
             const langs = ['en', 'ar', 'fr', 'es', 'ur'];
             const idx = langs.indexOf(lang);
             const next = langs[(idx + 1) % langs.length];
             setLang(next as Language);
           }}
           className="w-8 h-8 flex items-center justify-center hover:bg-stone-100 dark:hover:bg-slate-800 rounded-full transition-colors text-base border border-stone-200 dark:border-slate-700"
           title="Switch Language"
         >
           {lang === 'en' && '🇬🇧'}
           {lang === 'ar' && '🇸🇦'}
           {lang === 'fr' && '🇫🇷'}
           {lang === 'es' && '🇪🇸'}
           {lang === 'ur' && '🇵🇰'}
         </button>

         {/* Profile Circle */}
         <button 
           onClick={onProfileClick}
           className="w-9 h-9 rounded-full overflow-hidden border-2 border-emerald-100 hover:border-emerald-300 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
         >
           {profile.avatar ? (
             <img src={profile.avatar} alt="Profile" className="w-full h-full object-cover" />
           ) : (
             <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400">
               <User className="w-5 h-5" />
             </div>
           )}
         </button>
      </div>
    </nav>
  );
}
